import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Modal,
  Row,
  Col,
  Layout,
  Card,
  Select,
  DatePicker,
  Table,
  Collapse,
} from "antd";
import {
  ConsoleSqlOutlined,
  DownOutlined,
  FileSearchOutlined,
  EyeOutlined,
  FilterOutlined,
  RedoOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import { ReactSVG } from "react-svg";
import { wrapRequestPayload } from "../../../../helpers/request/RequestPayload";
import {
  getAggregateBdsPublishTopic,
  getAggregateBdsSubscriptionTopic,
  getAdminRolesTopic,
  getProjectsSubscriptionTopic,
  getProjectsRequestPublishTopic,
  getUsersSubscriptionTopic,
  getUserRequestPublishTopic,
  getMemberPageUpdatedPublishTopic,
  getFocusPagePublishTopic,
  getRideInvitationSubscriptionTopic,
} from "hooks/MQTTConstants";
import { useLocation } from "react-router-dom";
import { SocketTopics, UserTopics } from "constants/enums";
import { useSelector } from "react-redux";
import { RootState } from "reducers/rootReducer";
import { useDispatch } from "react-redux";
import VerticalGraph from "common/Graphs/VerticalGraph";
import useSocketActions from "../../../../hooks/socketActions";
import { generateUUID, popupCenter } from "utils/appUtil";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { formatForRedis, attributeKeyGenerator } from "utils/appUtil";
import { GET_AGGREGATE, DECRYPT_TOKEN } from "../../../../reducers/reducer";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { throttle, debounce } from "throttle-debounce";
import "./Dashboard.scss";

const { Panel } = Collapse;

ChartJS.register(ArcElement, Tooltip, Legend);

const { Option } = Select;
let typingTimeout;

const DashboardModal: FC<{
  socket: ISocket;
  params?: any;
  filter?: boolean;
  selected?: string;
  componentUuid?: string;
}> = ({ socket, params, filter, selected, componentUuid }) => {
  const { search } = useLocation();
  const {
    userRequests,
    projects,
    pageAggregateBds,
    selectedPage,
    encrypted_token,
    searchByPhone,
  } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [projectFilter, setProjectFilter] = useState<string>("project");
  const [timeFilter, setTimeFilter] = useState<string>("state");
  const [projectFilterLoading, setProjectFilterLoading] =
    useState<boolean>(false);
  const [projectFilterValue, setProjectFilterValue] = useState<any>(undefined);
  const [timeFilterValue, setTimeFilterValue] = useState<any>(undefined);
  const [statusFilterLoading, setStatusFilterLoading] =
    useState<boolean>(false);
  const [timeFilterLoading, setTimeFilterLoading] = useState<boolean>(false);
  const [projectFilterOptions, setProjectFilterOptions] = useState<any[]>([]);
  const [timeFilterOptions, setTimeFilterOptions] = useState<any[]>([]);
  const [pages, setPages] = useState<any>([]);
  const [pageLocations, setPageLocations] = useState<any>([]);
  const filterUserRequestId = useRef(generateUUID());
  const pageUserRequestId = useRef(generateUUID());
  const startDate = useRef<any>(undefined);
  const endDate = useRef<any>(undefined);
  const pageOffset = useRef<any>(0);
  const selectedColor = useRef<any>(undefined);
  const [pageUpdated, setPageUpdated] = useState<any>();
  const [statusCount, setStatusCount] = useState<any>({
    all: 0,
    live: 0,
    amber: 0,
    purple: 0,
    red: 0,
    green: 0,
    blue: 0,
    grey: 0,
    black: 0,
    brown: 0,
    aqua: 0,
  });
  const [redrawCount, setRedrawCount] = useState<number>(0);
  const [tableDataSource, setTableDataSource] = useState<any>([]);
  const tableDataSourceRef = useRef(undefined);
  const usersRef = useRef(undefined);
  const [tableColumns, setTableColumns] = useState<any>([]);
  const [showTableFilterModal, setShowTableFilterModal] =
    useState<boolean>(false);
  const [tableHideList, setTableHideList] = useState<any>([]);
  const [statusSelected, setStatusSelected] = useState<any>(undefined);
  const { getUserRequestPayload } = useSocketActions();
  const adminRoles = useRef(undefined);
  const pagePaths = useRef({
    project: undefined,
    user: undefined,
    status: undefined,
    area: undefined,
    time: undefined,
    phone: undefined,
  });
  const pageThrottleRef = useRef({});
  const encryptedTokenRef = useRef<any>();

  const loadPaths = useRef<any>([]);

  const getPageStatusthrottleFunc = useCallback(
    debounce(
      2000,
      () => {
        console.log("debounce");
        // Some API call
        getPageStatus();
      },
      { atBegin: true }
    ),
    []
  );

  const getPagethrottleFunc = useCallback((pageId) => {
    if (!pageThrottleRef.current?.[pageId]) {
      pageThrottleRef.current[pageId] = debounce(
        2000,
        (pageId) => {
          console.log("search page here", pageId);
          getPageById(pageId);
        },
        { atBegin: false }
      );
    }
    pageThrottleRef.current?.[pageId]?.(pageId);
  }, []);

  // const getPageStatusthrottleFunc = throttle(
  //   2000,
  //   (num) => {
  //     console.log('throttle');
  //     getPageStatus();
  //   },
  //   { noLeading: false, noTrailing: false }
  // );

  const setProjectPagePaths = (value) => {
    setProjectFilterValue(value);
    switch (projectFilter) {
      case "project":
        pagePaths.current.project = value
          ? `exists(@iotAssetConfigId) && @iotAssetConfigId === '${value}'`
          : undefined;
        pagePaths.current.user = undefined;
        setProjectFilterLoading(true);
        break;
      case "manager":
      case "responder":
      case "dispatcher":
        loadPaths.current = [
          {
            key: "user",
            value: `$.persistent.static.metadata.members.users.${value}`,
          },
        ];
        pagePaths.current.user = value ? `exists(@user)` : undefined;
        pagePaths.current.project = undefined;
        setProjectFilterLoading(true);
        break;
    }

    searchPage();
  };

  const setStatusPagePaths = (value) => {
    pagePaths.current.status = value
      ? `exists(@status) && @status === '${value}'`
      : undefined;
    pagePaths.current.phone = undefined;
    setStatusFilterLoading(true);
    selectedColor.current = value;
    setStatusSelected(value);
    searchPage();
  };

  const setPhonePagePaths = (value) => {
    pagePaths.current.phone = value
      ? `exists(@phone) && contains(@phone, '${value}')`
      : undefined;
    setStatusFilterLoading(true);
    pagePaths.current.status = undefined;
    selectedColor.current = "lime";
    setStatusSelected("lime");
    searchPage();
  };

  useEffect(() => {
    if (searchByPhone) {
      console.log("search by phone", searchByPhone);
      setPhonePagePaths(searchByPhone?.phone);
    }
  }, [searchByPhone]);

  const setAreaPagePaths = (value) => {
    setTimeFilterValue(value);
    pagePaths.current.time = value
      ? `exists(@${timeFilter}) && @${timeFilter} === '${value}'`
      : undefined;
    setTimeFilterLoading(true);

    searchPage();
  };

  const setTimePagePaths = (type, value) => {
    if (type === "start") {
      startDate.current = value;
    } else {
      endDate.current = value;
    }
    if (startDate.current && endDate.current) {
      const start = new Date(startDate.current);
      start.setHours(0);
      start.setMinutes(1);
      const end = new Date(endDate.current);
      end.setHours(23);
      end.setMinutes(59);
      pagePaths.current.time = `exists(@createdAt) && @createdAt > ${start.getTime()} && @createdAt < ${end.getTime()}`;
      setTimeFilterLoading(true);
      searchPage();
    } else if (!startDate.current && !startDate.current) {
      pagePaths.current.time = undefined;
      setTimeFilterLoading(true);
      searchPage();
    }
  };

  const handleProjectFilter = (type) => {
    if (projectFilter === type || !projects?.projects || !adminRoles.current)
      return;
    setProjectFilterValue(undefined);
    setProjectFilterOptions([]);
    const options = [];
    switch (type) {
      case "project":
        projects?.projects?.map((project) => {
          options.push({ label: project.name, value: project.id });
        });
        setProjectFilterOptions(options);
        break;
      case "manager":
        getUsersByRole(adminRoles.current, "manager");
        break;
      case "dispatcher":
        getUsersByRole(adminRoles.current, "dispatcher");
        break;
      case "responder":
        getUsersByRole(adminRoles.current, "responder");
        break;
    }
    setProjectFilter(type);
  };

  const handleTimeFilter = (type) => {
    if (timeFilter === type) return;
    setTimeFilterValue(undefined);
    setTimeFilterOptions([]);
    getPageAggregateBds(type);
    setTimeFilter(type);
  };

  const getAdminRoles = (adminUserId, cudbServiceId) => {
    if (adminRoles.current) return;
    // const query = new URLSearchParams(search);
    // const connectionId = query.get("connectionId");
    socket.publishToServiceIdSurvey(
      SocketTopics.SurveyTopics,
      UserTopics.SearchInUser,
      getUserRequestPayload(adminUserId, "$", "$..role"),
      getAdminRolesTopic(encrypted_token?.connectionId),
      cudbServiceId
    );
    socket.subscribeToSurvey(
      SocketTopics.SurveyTopics,
      getAdminRolesTopic(encrypted_token?.connectionId),
      (response) => {
        adminRoles.current = response.data?.[0]?.[0];
        // getUsersByRole(adminRoles.current, "responder");
      },
      componentUuid
    );
  };

  const getUsersByRole = (roles, type) => {
    // const query = new URLSearchParams(search);
    // const connectionId = query.get("connectionId");
    let userRoles;
    switch (type) {
      case "dispatcher":
        userRoles = roles.filter(
          (x: any) =>
            x.permissions.indexOf("cad") !== -1 &&
            x.permissions.indexOf("admin") === -1
        );
        break;
      case "responder":
        userRoles = roles.filter(
          (x: any) =>
            x.permissions.indexOf("onMove") !== -1 &&
            x.permissions.indexOf("admin") === -1
        );
        break;
      case "manager":
        userRoles = roles.filter(
          (x: any) =>
            x.permissions.indexOf("cad") !== -1 &&
            x.permissions.indexOf("onMove") !== -1 &&
            x.permissions.indexOf("admin") === -1
        );
        break;
    }
    const rolesQuery = userRoles.map((y: any) => {
      return `@roleId === '${y._id}'`;
    });
    const userRequest: any = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:userNameIndex`,
          searchQuery: `@serviceId:{'${formatForRedis(
            projects?.userServiceId
          )}'}`,
          searchOptions: "TIMEOUT 5000",
        },
        load: {
          id: "$.objectId",
          status: "$.nonpersistent.static.live.status",
          live: "$.persistent.static.live",
          roleId: "$..admin.profile.roleId",
        },
      },
      apply: {
        expression: "",
        name: "",
      },
      result: {},
      filter: {
        expression: `exists(@roleId) && (${rolesQuery.join("||")})`,
      },
      anyStatus: false,
    };
    socket.publishToEvent(
      SocketTopics.PublishPageTopics,
      getAggregateBdsPublishTopic(encrypted_token?.connectionId),
      wrapRequestPayload(userRequest, `${filterUserRequestId.current}:${type}`)
    );
    setProjectFilterLoading(true);
  };

  const getPageAggregateBds = (aggregateBy) => {
    // const query = new URLSearchParams(search);
    // const userId = query.get("userId");
    setTimeFilterLoading(true);
    const pageAggregateRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${encrypted_token?.userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        load: {
          [aggregateBy]: `$.persistent.static.profile.${aggregateBy}`,
        },
      },
      groupBy: {
        properties: [`@${aggregateBy}`],
        reducers: [
          {
            func: "COUNT",
            arguments: [],
            name: "count",
          },
        ],
      },
      filter: {
        expression: "",
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: pageAggregateRequest,
        callback: (response) => {
          setTimeFilterLoading(false);
          let options = [];
          response?.map((aggregateBy) => {
            if (aggregateBy[Object.keys(aggregateBy)[0]]) {
              options.push({
                label: aggregateBy[Object.keys(aggregateBy)[0]],
                value: aggregateBy[Object.keys(aggregateBy)[0]],
              });
            }
          });
          setTimeFilterOptions(options);
        },
      })
    );
  };

  const getPageById = (pageId) => {
    if (typeof pageId !== "string") return;
    // const query = new URLSearchParams(search);
    // const userId = query.get("userId");
    const pageAggregateRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${encryptedTokenRef.current?.userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        list: {
          objectIds: [pageId],
        },
        load: {
          persistent: "$.persistent",
          nonpersistent: "$.nonpersistent",
          pageId: "$.objectId",
        },
      },
      filter: {
        expression: "",
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: pageAggregateRequest,
        callback: (response) => {
          setPageUpdated(response?.[0]);
        },
      })
    );
  };

  const getPageStatus = () => {
    // const query = new URLSearchParams(search);
    // const userId = query.get("userId");
    const expressions = [
      "exists(@profile)",
      "exists(@type)",
      "@type==='event'",
    ];
    if (!pagePaths.current.time) {
      const date = new Date();
      date.setHours(0);
      date.setMinutes(1);
      expressions.push(`exists(@createdAt) && @createdAt > ${date.getTime()}`);
    } else {
      expressions.push(pagePaths.current.time);
    }

    const pageStatusCountRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${encryptedTokenRef?.current?.userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        load: {
          profile: "$..profile",
          type: "$..persistent.static.metadata.type",
          persistent: "$.persistent",
          nonpersistent: "$.nonpersistent",
          pageId: "$.objectId",
          iotAssetConfigId:
            "$.persistent.static.iotAsset.config.iotAssetConfigId",
          createdAt: "$.persistent.static.metadata.createdAt",
          status: "$.nonpersistent.static.live.status.name",
        },
      },
      groupBy: {
        properties: ["@status"],
        reducers: [
          {
            func: "COUNT",
            arguments: [],
            name: "count",
          },
        ],
      },
      filter: {
        expression: expressions.join("&&"),
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: pageStatusCountRequest,
        callback: (response) => {
          let all = 0;
          let status: any = {
            amber: 0,
            purple: 0,
            red: 0,
            green: 0,
            blue: 0,
            grey: 0,
            black: 0,
            brown: 0,
            aqua: 0,
          };
          response.map((x) => {
            status[x.status] = x.count;
            all += x.count;
          });
          setStatusCount({
            all,
            live: all,
            ...status,
          });
        },
      })
    );
  };

  useEffect(() => {
    if (statusCount) {
      setRedrawCount(redrawCount + 1);
    }
  }, [JSON.stringify(statusCount)]);

  useEffect(() => {
    if (pageUpdated) {
      
      const profile = {
        ...(pageUpdated?.persistent?.static?.profile || {}),
        ...(pageUpdated?.persistent?.static?.dashboard || {}),
        ...(pageUpdated?.persistent?.static?.ride || {}),
        ...(pageUpdated?.persistent?.dynamic?.ride || {}), 
      };
      
      const status = pageUpdated?.nonpersistent?.static?.live?.status?.name;
      let tempPages = [...tableDataSource];
      let index = tempPages.findIndex((page) => {
        return page.key === pageUpdated.pageId;
      });

      // getPageStatus();
      getPageStatusthrottleFunc();

      if (selectedColor.current && selectedColor.current !== status) {
        if (index !== -1) {
          tempPages.splice(index, 1);
          setTableDataSource(tempPages);
          tableDataSourceRef.current = tempPages;
        }
        return;
      }
      const source: any = {};
      Object.keys(profile).map((p) => {
        if (typeof profile[p] === "string") {
          source[p] = profile[p];
        }
      });
      const sourceUpdatedData = {
        key: pageUpdated.pageId,
        pageStatus: pageUpdated?.nonpersistent?.static?.live?.status?.name,
        name: pageUpdated?.persistent?.static?.metadata?.title,
        action: {
          pageId: pageUpdated.pageId,
          page: {
            ...pageUpdated,
            profile,
          },
          summary: source.summary,
          cudbServiceId: projects?.userServiceId,
        },
        ...source,
        address:
          source?.address && source?.adress !== ""
            ? source?.address
            : profile?.pickupLocation?.attributes?.address,
        dropoffAddress:
          source?.dropoffAddress && source?.dropoffAddress !== ""
            ? source?.dropoffAddress
            : profile?.dropoffLocation?.attributes?.address,
      };

      console.log('source updated', sourceUpdatedData);
      if (index !== -1) {
        tempPages[index] = {
          ...sourceUpdatedData,
          invited: tableDataSourceRef?.current?.[index]?.invited,
        };
      } else tempPages.unshift(sourceUpdatedData);
      setPageLocations(
        tempPages?.map((x) => {
          return {
            lat: x?.action?.page?.persistent?.static?.live?.location
              ?.coordinates?.lat,
            lng: x?.action?.page?.persistent?.static?.live?.location
              ?.coordinates.long,
            title: x?.action?.page?.persistent?.static?.metadata?.title,
          };
        })
      );
      setTableDataSource(tempPages);
      tableDataSourceRef.current = tempPages;
    }
  }, [pageUpdated]);

  // const searchPage = (offset = 0) => {
  //   // const query = new URLSearchParams(search);
  //   // const userId = query.get("userId");
  //   const expressions = [
  //     "exists(@profile)",
  //     "exists(@type)",
  //     "@type==='event'",
  //   ];
  //   if (selected === "advance") {
  //     expressions.push(`exists(@status) && @status ==='brown'`);
  //     selectedColor.current = "brown";
  //   } else if (selected === "dispatch") {
  //     expressions.push(`exists(@status) && @status ==='purple'`);
  //     selectedColor.current = "purple";
  //   } else if (pagePaths.current) {
  //     Object.keys(pagePaths.current).map((key) => {
  //       if (pagePaths.current?.[key]) {
  //         expressions.push(pagePaths.current?.[key]);
  //       }
  //     });
  //     if (!pagePaths.current.time && !pagePaths.current.phone) {
  //       const date = new Date();
  //       date.setHours(0);
  //       date.setMinutes(1);
  //       expressions.push(
  //         `exists(@createdAt) && @createdAt > ${date.getTime()}`
  //       );
  //     }
  //   }
  //   const load = {};
  //   if (loadPaths.current) {
  //     loadPaths.current.map((x) => {
  //       load[x.key] = x.value;
  //     });
  //   }

  //   pageOffset.current = offset;

  //   const getPagesRequest = {
  //     select: {
  //       index: {
  //         searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
  //         searchQuery: `@pageMembers:{'${encrypted_token?.userId}'}`,
  //         searchOptions: "VERBATIM TIMEOUT 10000",
  //       },
  //       load: {
  //         profile: "$..profile",
  //         dashboard: "$..dashboard",
  //         ride: "$.persistent.static.ride",
  //         dynamic: "$.persistent.dynamic.ride",
  //         type: "$..persistent.static.metadata.type",
  //         // persistent: "$.persistent",
  //         // nonpersistent: "$.nonpersistent",
  //         pageId: "$.objectId",
  //         iotAssetConfigId:
  //           "$.persistent.static.iotAsset.config.iotAssetConfigId",
  //         createdAt: "$.persistent.static.metadata.createdAt",
  //         updatedAt:
  //           "$.nonpersistent.dynamic.metadata.lastSent.members.updatedAt",
  //         status: "$.nonpersistent.static.live.status.name",
  //         phone: "$..profile.passengerPhoneNumber",
  //         [timeFilter]: `$..profile.${timeFilter}`,
  //         ...load,
  //       },
  //     },
  //     filter: {
  //       expression: expressions.join("&&"),
  //     },
  //     sortBy: {
  //       properties: [
  //         selected === "advance"
  //           ? {
  //               field: "@createdAt",
  //               order: "ASC",
  //             }
  //           : {
  //               field: "@updatedAt",
  //               order: "DESC",
  //             },
  //       ],
  //       max: 1000,
  //     },
  //     result: pagePaths.current.phone
  //       ? {
  //           pagination: {
  //             offset: offset,
  //             limit: 50,
  //           },
  //         }
  //       : {},
  //   };

  //   console.log("page request", getPagesRequest);

  //   dispatch(
  //     GET_AGGREGATE({
  //       requestData: getPagesRequest,
  //       callback: (response) => {
  //         console.log("resppnse", response);
  //         setPages(response);
  //         pageOffset.current = pageOffset.current + 30;
  //         let sourceData = offset > 0 ? [...tableDataSource] : [];
  //         let columns: any = [
  //           {
  //             title: "Status",
  //             key: "pageStatus",
  //             dataIndex: "pageStatus",
  //             render: (status: string) => (
  //               <Button className="beam-btn icon-btn ml-2">
  //                 <ReactSVG
  //                   src="/images/flag-icon.svg"
  //                   className={`${status ?? "gray"}-icon`}
  //                 />
  //               </Button>
  //             ),
  //           },
  //           {
  //             title: "Schedule Date",
  //             key: "scheduleDate",
  //             dataIndex: "scheduleDate",
  //           },
  //           {
  //             title: "Schedule Time",
  //             key: "scheduleTime",
  //             dataIndex: "scheduleTime",
  //           },
  //           {
  //             title: "Pickup Address",
  //             key: "address",
  //             dataIndex: "address",
  //           },
  //           {
  //             title: "Dropoff Affress",
  //             key: "dropoffAddress",
  //             dataIndex: "dropoffAddress",
  //           },
  //           {
  //             title: "Passenger Name",
  //             key: "passengerName",
  //             dataIndex: "passengerName",
  //           },
  //           {
  //             title: "Passenger Phone",
  //             key: "passengerPhoneNumber",
  //             dataIndex: "passengerPhoneNumber",
  //           },
  //           {
  //             title: "Account name",
  //             key: "accountName",
  //             dataIndex: "accountName",
  //           },
  //           {
  //             title: "Driver Name",
  //             key: "driverName",
  //             dataIndex: "driverName",
  //           },
  //           {
  //             title: "Vehicle Name",
  //             key: "vehicleName",
  //             dataIndex: "vehicleName",
  //           },
  //           {
  //             title: "Fixed Fare",
  //             key: "fixedFare",
  //             dataIndex: "fixedFare",
  //           },
  //           {
  //             title: "Invited Driver",
  //             key: "invited",
  //             dataIndex: "invited",
  //           },
  //         ];
  //         response?.map((x, i) => {
  //           const profile = {
  //             ...(x?.profile || {}),
  //             ...(x?.dashboard || {}),
  //             ...(x?.ride || {}),              
  //             ...(x?.dynamic || {}),    
  //           };
  //           const source: any = {};
  //           Object.keys(profile).map((p) => {
  //             if (typeof profile[p] === "string") {
  //               source[p] = profile[p];
  //             }
  //           });
  //           sourceData.push({
  //             key: x.pageId,
  //             pageStatus: x.status,
  //             name: "",
  //             action: {
  //               pageId: x.pageId,
  //               page: {
  //                 ...x,
  //                 profile,
  //               },
  //               summary: source.summary,
  //               cudbServiceId: projects?.userServiceId,
  //             },
  //             ...source,
  //             address:
  //               source?.address && source?.adress !== ""
  //                 ? source?.address
  //                 : profile?.pickupLocation?.attributes?.address,
  //             dropoffAddress:
  //               source?.dropoffAddress && source?.dropoffAddress !== ""
  //                 ? source?.dropoffAddress
  //                 : profile?.dropoffLocation?.attributes?.address,
  //             invited: "",
  //           });
  //         });
  //         console.log("source data", sourceData);
  //         sourceData.map((x) => {
            
  //           const profile = {
  //             ...(x?.profile || {}),
  //             ...(x?.dashboard || {}),
  //             ...(x?.ride || {}),              
  //             ...(x?.dynamic || {}),    
  //           };


  //           Object.keys(profile || {}).map((p) => {
  //             if (
  //               columns.findIndex((c) => {
  //                 return c.key === p;
  //               }) === -1 &&
  //               typeof profile[p] === "string"
  //             ) {
  //             }
  //           });
  //         });

  //         columns.push({
  //           title: "Action",
  //           key: "action",
  //           dataIndex: "action",
  //           render: (action: any) => (
  //             <Row style={{ width: 90 }}>
  //               <Col span={12}>
  //                 <Button
  //                   className="beam-btn icon-btn"
  //                   onClick={() => {
  //                     popupCenter(
  //                       `${process.env.REACT_APP_WEB}/page/${action?.pageId}`,
  //                       "BeamLive",
  //                       window.innerWidth * 0.7,
  //                       window.innerHeight * 0.9
  //                     );
  //                   }}
  //                 >
  //                   <MessageOutlined />
  //                 </Button>
  //               </Col>

  //               <Col span={12}>
  //                 {action?.summary && (
  //                   <Button
  //                     className="beam-btn icon-btn ml-2"
  //                     onClick={() => {
  //                       // const query = new URLSearchParams(search);
  //                       // const connectionId = query.get("connectionId");
  //                       window.open(
  //                         `${window.location.origin}/${
  //                           action?.summary ?? "summary"
  //                         }?connectionId=${
  //                           encrypted_token?.connectionId
  //                         }&pageId=${action?.pageId}&cudbServiceId=${
  //                           action?.cudbServiceId
  //                         }`,
  //                         "_blank"
  //                       );
  //                     }}
  //                   >
  //                     <FileSearchOutlined />
  //                   </Button>
  //                 )}
  //               </Col>
  //             </Row>
  //           ),
  //         });
  //         setTableColumns(columns);
  //         setTableDataSource(sourceData);
  //         tableDataSourceRef.current = sourceData;
  //         setPageLocations(
  //           response?.map((x) => {
  //             return {
  //               lat: x?.persistent?.static?.live?.location?.coordinates?.lat,
  //               lng: x?.persistent?.static?.live?.location?.coordinates.long,
  //               title: x?.persistent?.static?.metadata?.title,
  //             };
  //           })
  //         );
  //         setStatusFilterLoading(false);
  //         setProjectFilterLoading(false);
  //         setTimeFilterLoading(false);
  //       },
  //     })
  //   );
  //   // getPageStatus();
  //   getPageStatusthrottleFunc();
  // };

  const searchPage = (offset = 0) => {
    // const query = new URLSearchParams(search);
    // const userId = query.get("userId");
    const expressions = [
      "exists(@profile)",
      "exists(@type)",
      "@type==='event'",
    ];
    if (pagePaths.current) {
      Object.keys(pagePaths.current).map((key) => {
        if (pagePaths.current?.[key]) {
          expressions.push(pagePaths.current?.[key]);
        }
      });
    }
    const load = {};
    if (loadPaths.current) {
      loadPaths.current.map((x) => {
        load[x.key] = x.value;
      });
    }

    pageOffset.current = offset;

    const getPagesRequest = {
      select: {
        index: {
          searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageUpdatedAtIndex`,
          searchQuery: `@pageMembers:{'${encrypted_token?.userId}'}`,
          searchOptions: "VERBATIM TIMEOUT 10000",
        },
        load: {
          profile: "$..profile",
          type: "$..persistent.static.metadata.type",
          persistent: "$.persistent",
          nonpersistent: "$.nonpersistent",
          pageId: "$.objectId",
          iotAssetConfigId:
            "$.persistent.static.iotAsset.config.iotAssetConfigId",
          createdAt: "$.persistent.static.metadata.createdAt",
          updatedAt:
            "$.nonpersistent.dynamic.metadata.lastSent.members.updatedAt",
          status: "$.nonpersistent.static.live.status.name",
          [timeFilter]: `$..profile.${timeFilter}`,
          ...load,
        },
      },
      filter: {
        expression: expressions.join("&&"),
      },
      sortBy: {
        properties: [
          {
            field: "@updatedAt",
            order: "DESC",
          },
        ],
        max: 10,
      },
      result: {
        pagination: {
          offset: offset,
          limit: 30,
        },
      },
    };
    dispatch(
      GET_AGGREGATE({
        requestData: getPagesRequest,
        callback: (response) => {
          // if (!setSelectedPageRef.current) {
          //   setSelectedPage(response?.[0]);
          //   setSelectedPageRef.current = response?.[0];
          //   getPageUsers(response?.[0]);
          // } else {
          //   const selected = response.find((x) => {
          //     return x.pageId === setSelectedPageRef.current?.pageId;
          //   });
          //   if (selected) {
          //     setSelectedPage(selected);
          //     setSelectedPageRef.current = selected;
          //     getPageUsers(selected);
          //   } else {
          //     setSelectedPage(response?.[0]);
          //     setSelectedPageRef.current = response?.[0];
          //     getPageUsers(response?.[0]);
          //   }
          // }

          setPages(response);
          pageOffset.current = pageOffset.current + 30;

          let sourceData = offset > 0 ? [...tableDataSource]: [];
          let columns: any = [
            {
              title: "Status",
              key: "pageStatus",
              dataIndex: "pageStatus",
              render: (status: string) => (
                <Button className="beam-btn icon-btn ml-2">
                  <ReactSVG
                    src="/images/flag-icon.svg"
                    className={`${status ?? "gray"}-icon`}
                  />
                </Button>
              ),
            },
            {
              title: "Name",
              key: "name",
              dataIndex: "name",
            },
          ];
          response?.map((x, i) => {
            const profile = x?.persistent?.static?.profile;
            const source: any = {};
            Object.keys(profile).map((p) => {
              // if (
              //   columns.findIndex((c) => {
              //     return c.key === p;
              //   }) === -1 &&
              //   typeof profile[p] === "string"
              // ) {
              //   columns.push({
              //     title: attributeKeyGenerator(p),
              //     dataIndex: p,
              //     key: p,
              //   });
              // }
              if (typeof profile[p] === "string") {
                source[p] = profile[p];
              }
            });
            sourceData.push({
              key: x.pageId,
              pageStatus: x?.nonpersistent?.static?.live?.status?.name,
              name: x?.persistent?.static?.metadata?.title,
              action: {
                pageId: x.pageId,
                page: x,
                summary: source.summary,
                cudbServiceId: projects?.userServiceId,
              },
              ...source,
            });
          });
          console.log('source data', sourceData.length)
          sourceData.map((x)=>{
            const profile = x?.action?.page?.persistent?.static?.profile;
            Object.keys(profile).map((p) => {
              if (
                columns.findIndex((c) => {
                  return c.key === p;
                }) === -1 &&
                typeof profile[p] === "string"
              ) {
                columns.push({
                  title: attributeKeyGenerator(p),
                  dataIndex: p,
                  key: p,
                });
              }
            });
          })

          columns.push({
            title: "Action",
            key: "action",
            dataIndex: "action",
            render: (action: any) => (
              <Row style={{ width: 90 }}>
                <Col span={12}>
                  <Button
                    className="beam-btn icon-btn"
                    onClick={() => {
                      // const query = new URLSearchParams(search);
                      // const userId = query.get("userId");
                      socket.publishToEvent(
                        SocketTopics.PublishPageTopics,
                        getFocusPagePublishTopic(action?.pageId, encrypted_token?.userId),
                        {
                          page: action?.page,
                        }
                      );
                    }}
                  >
                    <EyeOutlined />
                  </Button>
                </Col>

                <Col span={12}>
                  {action?.summary && (
                    <Button
                      className="beam-btn icon-btn ml-2"
                      onClick={() => {
                        // const query = new URLSearchParams(search);
                        // const connectionId = query.get("connectionId");
                        window.open(
                          `${window.location.origin}/${
                            action?.summary ?? "summary"
                          }?connectionId=${encrypted_token?.connectionId}&pageId=${
                            action?.pageId
                          }&cudbServiceId=${action?.cudbServiceId}`,
                          "_blank"
                        );
                      }}
                    >
                      <FileSearchOutlined />
                    </Button>
                  )}
                </Col>
              </Row>
            ),
          });
          setTableColumns(columns);
          console.log("source data", sourceData);

          setTableDataSource(sourceData);
          setPageLocations(
            response?.map((x) => {
              return {
                lat: x?.persistent?.static?.live?.location?.coordinates?.lat,
                lng: x?.persistent?.static?.live?.location?.coordinates.long,
                title: x?.persistent?.static?.metadata?.title,
              };
            })
          );
          setStatusFilterLoading(false);
          setProjectFilterLoading(false);
          setTimeFilterLoading(false);
        },
      })
    );
    getPageStatusthrottleFunc();
  };

  

  useEffect(() => {
    if (encrypted_token) {
      // const query = new URLSearchParams(search);
      // const userId = query.get("userId");
      // const connectionId = query.get("connectionId");
      encryptedTokenRef.current = encrypted_token;
      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getProjectsSubscriptionTopic(encrypted_token?.connectionId)
      );
      socket.publishToEvent(
        SocketTopics.PublishPageTopics,
        getProjectsRequestPublishTopic(encrypted_token?.connectionId),
        wrapRequestPayload({
          userId: encrypted_token?.userId,
        })
      );

      socket.subscribeToEvent(
        SocketTopics.SubscribePageTopics,
        getMemberPageUpdatedPublishTopic(encrypted_token?.userId),
        (response) => {
          // console.log('get page by id', response);
          // getPageById(response?.data?.pageId);
          getPagethrottleFunc(response?.data);
        },
        componentUuid
      );

      // socket.subscribeToEvent(
      //   SocketTopics.SubscribePageTopics,
      //   getRideInvitationSubscriptionTopic(),
      //   (response) => {
      //     console.log('ride invitation get', response);

      //     const userId = response?.pattern?.split('/')?.[4];
      //     console.log('user id', userId);
      //     const pageId= response?.data?.pageId;
      //     const timeout = response?.data?.timeout * 1000;
      //     const cudbServiceId = response?.data?.rideRequestAttributes?.cudbServiceId;

      //     if(projects?.userServiceId)

      //     const testResponse = {
      //       "pattern": "beamdevlive/service/ride-dps/user/103146/ridePageInvitation",
      //       "data": {
      //           "pageId": "140577",
      //           "type": "broadcast",
      //           "timeout": 10,
      //           "rideRequestAttributes": {
      //               "passengerPhoneNumber": "+103334518337",
      //               "pickupLocation": {
      //                   "attributes": {
      //                       "address": "Askari 10, Lahore, Pakistan"
      //                   },
      //                   "coordinates": {
      //                       "lat": 31.5356589,
      //                       "long": 74.4144562
      //                   },
      //                   "type": "incident-location"
      //               },
      //               "dispatch": {
      //                   "type": "dispatchType",
      //                   "value": "immediate"
      //               },
      //               "rideType": {
      //                   "type": "rideType",
      //                   "value": "oneWay"
      //               },
      //               "fareType": "variable",
      //               "numberOfPeople": "1",
      //               "numberOfLuggage": "1",
      //               "driverGender": "male",
      //               "drivers": [],
      //               "pickupAddress": "Askari 10, Lahore, Pakistan",
      //               "maskedPickupLocation": {
      //                   "attributes": {
      //                       "address": "Unknown"
      //                   },
      //                   "coordinates": {
      //                       "lat": 31.539404874734775,
      //                       "long": 74.41885125998472
      //                   }
      //               },
      //               "maskedPickupAddress": "Unknown",
      //               "cudbServiceId": "cudb-test"
      //           },
      //           "estimation": {
      //               "estimate": 0,
      //               "distance": 0,
      //               "duration": 0,
      //               "shift": null
      //           }
      //       },
      //       "id": "9c3a775c-9dcc-46e7-b3c0-368065a6ab64"
      //   }

      //   },
      //   componentUuid
      // );

      setProjectFilterLoading(true);
      searchPage();
      getPageAggregateBds("state");
      setTableHideList(JSON.parse(localStorage.getItem("tableHideList")) || []);
    }
  }, [encrypted_token]);

  useEffect(() => {
    if (socket.client) {
      const query = new URLSearchParams(search);
      const userId = query.get("userId");
      const connectionId = query.get("connectionId");
      const token = query.get("token");
      if (token) {
        dispatch(
          DECRYPT_TOKEN({
            data: { payload: token },
            callback: (data) => {},
          })
        );
      } else {
        dispatch({
          type: "ADD_ENCRYPTED_TOKEN",
          payload: {
            connectionId,
            userId,
          },
        });
      }
    }
  }, [search, socket.client]);

  useEffect(() => {
    if (projects) {
      // const query = new URLSearchParams(search);
      // const connectionId = query.get("connectionId");
      const options = [];
      projects?.projects?.map((project) => {
        options.push({ label: project.name, value: project.id });
      });
      setProjectFilterOptions(options);
      if (projects?.adminUserId) {
        getAdminRoles(projects?.adminUserId, projects?.userServiceId);
        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          getUsersSubscriptionTopic(
            encrypted_token?.connectionId,
            projects?.userServiceId
          )
        );
        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          getAggregateBdsSubscriptionTopic(encrypted_token?.connectionId)
        );

        socket.subscribeToEvent(
          SocketTopics.SubscribePageTopics,
          getRideInvitationSubscriptionTopic(),
          (response) => {
            console.log("ride invitation get", response);
            const userId = response?.pattern?.split("/")?.[4];
            const pageId = response?.data?.pageId;
            const timeout = response?.data?.timeout * 1000;
            const cudbServiceId =
              response?.data?.rideRequestAttributes?.cudbServiceId;
            if (projects?.userServiceId !== cudbServiceId) return;
            console.log("user id", userId, pageId, timeout);

            let userName =
              usersRef.current?.find((user) => {
                return user.id === userId;
              })?.beamId || `user# ${userId}`;
            let tempPages = [...(tableDataSourceRef.current || [])];
            let index = tempPages.findIndex((page) => {
              return page.key?.toString() === pageId?.toString();
            });
            if (index !== -1) {
              tempPages[index].invited = userName;
              tableDataSourceRef.current = tempPages;
              getPageById(pageId);
              setTimeout(() => {
                let tempPages = [...(tableDataSourceRef.current || [])];
                let index = tempPages.findIndex((page) => {
                  return page.key?.toString() === pageId?.toString();
                });
                if (index !== -1) {
                  console.log("clear here");
                  tempPages[index].invited = "";
                  tableDataSourceRef.current = tempPages;
                  getPageById(pageId);
                }
              }, timeout);
            }
          },
          componentUuid
        );
      }
    }
  }, [projects]);

  useEffect(() => {
    if (userRequests && socket.client) {
      if (!usersRef.current && userRequests?.[Object.keys(userRequests)?.[0]]) {
        console.log(
          "user requests here",
          userRequests?.[Object.keys(userRequests)?.[0]]
        );
        usersRef.current = userRequests?.[Object.keys(userRequests)?.[0]];
      }

      if (userRequests?.[`${filterUserRequestId.current}:${projectFilter}`]) {
        setProjectFilterLoading(false);
        let options = [];
        userRequests?.[`${filterUserRequestId.current}:${projectFilter}`]?.map(
          (user) => {
            options.push({
              label: `${user.fullName}: ${user.email}`,
              value: user.id,
            });
          }
        );
        setProjectFilterOptions(options);
      }
    }
  }, [userRequests, socket.client]);

  return (
    <React.Fragment>
      {/* {showTableFilterModal && ( */}
      <Row
        gutter={30}
        className="mb-2"
        style={showTableFilterModal ? {} : { display: "none" }}
      >
        <Col className="gutter-row" sm={24} md={10}>
          <Card className="filter-card">
            <Row gutter={16} className="d-flex px-1 flex-wrap">
              <div
              // sm={4}
              >
                <h5 className="mb-0">Filters</h5>
              </div>
              <div
                // sm={5}
                onClick={() => {
                  handleProjectFilter("manager");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "manager" ? "green-btn cover" : ""
                  }`}
                >
                  Manager
                </Button>
              </div>
              <div
                // sm={5}
                onClick={() => {
                  handleProjectFilter("project");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "project" ? "green-btn cover" : ""
                  }`}
                >
                  Project
                </Button>
              </div>
              <div
                // sm={5}
                onClick={() => {
                  handleProjectFilter("dispatcher");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "dispatcher" ? "green-btn cover" : ""
                  }`}
                >
                  Dispatcher
                </Button>
              </div>
              <div
                // sm={5}
                onClick={() => {
                  handleProjectFilter("responder");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    projectFilter === "responder" ? "green-btn cover" : ""
                  }`}
                >
                  Responder
                </Button>
              </div>
            </Row>
            <Row className="mt-3">
              <Col span={24}>
                <Select
                  className="w-full"
                  placeholder="Select ID"
                  loading={projectFilterLoading}
                  value={projectFilterValue}
                  allowClear
                  options={projectFilterOptions}
                  onChange={(value) => {
                    setProjectPagePaths(value);
                  }}
                ></Select>
              </Col>
            </Row>
            <Row gutter={15} className="d-flex px-2 flex-wrap mt-4">
              <Col sm={24} md={4}>
                <h5 className="mb-0">Status</h5>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col span={24}>
              <Select
                      className="w-full"
                      placeholder="Select ID"
                      loading={statusFilterLoading}
                      allowClear
                      onChange={(value) => {
                        setStatusPagePaths(value);
                      }}
                    >
                      <Option value={"purple"}>Created</Option>
                      <Option value={"blue"}>Started</Option>
                      <Option value={"amber"}>Assigned</Option>
                      <Option value={"red"}>Escalated</Option>
                      <Option value={"green"}>Completed</Option>
                      <Option value={"gray"}>Processed</Option>
                      <Option value={"black"}>Cancelled</Option>
                    </Select>

                  
              </Col>
            </Row>
          </Card>
        </Col>
        <Col className="gutter-row" sm={24} md={10}>
          <Card className="filter-card">
            <Row className="d-flex flex-wrap" id="filters">
              <div
              // sm={4}
              >
                <h5 className="mb-0">Filters</h5>
              </div>
              <div
                // sm={4}
                onClick={() => {
                  handleTimeFilter("state");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "state" ? "green-btn cover" : ""
                  }`}
                >
                  State
                </Button>
              </div>
              <div
                // sm={4}
                onClick={() => {
                  handleTimeFilter("city");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "city" ? "green-btn cover" : ""
                  }`}
                >
                  City
                </Button>
              </div>
              <div
                // sm={4}
                onClick={() => {
                  handleTimeFilter("county");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "county" ? "green-btn cover" : ""
                  }`}
                >
                  County
                </Button>
              </div>
              <div
                // sm={4}
                onClick={() => {
                  handleTimeFilter("zip");
                }}
              >
                <Button
                  className={`beam-btn w-70 ${
                    timeFilter === "zip" ? "green-btn cover" : ""
                  }`}
                >
                  Zip
                </Button>
              </div>
            </Row>
            <Row className="mt-3">
              <Col xs={24}>
                <Select
                  className="w-full"
                  placeholder="Select ID"
                  loading={timeFilterLoading}
                  allowClear
                  options={timeFilterOptions}
                  value={timeFilterValue}
                  onChange={(value) => {
                    setAreaPagePaths(value);
                  }}
                ></Select>
              </Col>
            </Row>
            <Row gutter={15} className="d-flex flex-wrap mt-3 mx-0">
              <Col xs={24} md={10} className="datetime-item">
                <img src="/images/time.png" alt="" />
                <DatePicker
                  placeholder="From"
                  suffixIcon={<DownOutlined />}
                  onChange={(time: any) => {
                    setTimePagePaths("start", time);
                  }}
                />
              </Col>
              <Col xs={24} md={4} className="d-flex justify-content-center">
                <ReactSVG src="/images/next.svg" />
              </Col>
              <Col xs={24} md={10} className="datetime-item">
                <img src="/images/time.png" alt="" />
                <DatePicker
                  placeholder="To"
                  suffixIcon={<DownOutlined />}
                  onChange={(time: any) => {
                    setTimePagePaths("end", time);
                  }}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col span={24}>
                <Select
                  className="w-full"
                  placeholder="Select Hour(s)"
                  disabled
                ></Select>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col className="gutter-row" sm={24} md={4}>
          <Card className="filter-card">
            <p className="mb-2 text-center">Export to</p>

            <div className="d-flex flex-row flex-md-column justify-content-center">
              <div>
                <Button
                  className="beam-btn export mb-0 mb-md-2 mr-3 mr-md-0"
                  disabled
                >
                  <ReactSVG src="/images/export-csv.svg" />
                </Button>
              </div>
              <div>
                <Button
                  className="beam-btn export mb-0 mb-md-2 mr-3 mr-md-0"
                  disabled
                >
                  <ReactSVG src="/images/export-xls.svg" />
                </Button>
              </div>
              <div>
                <Button
                  className="beam-btn export mb-0 mb-md-2 mr-3 mr-md-0"
                  disabled
                >
                  <ReactSVG src="/images/export-pdf.svg" />
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
      {/* )} */}
      <Row gutter={20} className="scrollable-table" style={{ display: "grid" }}>
        <Col
          xs={24}
          sm={24}
          md={24}
          style={{ maxHeight: 1000, overflow: "scroll", paddingRight: 0, paddingTop: 10 }}
        >
          {filter && (
            <Row className="d-flex mb-2">
              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === undefined ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths(undefined);
                }}
              >
                <div>
                  <span>All</span>
                  <h4>{statusCount.live}</h4>
                </div>
              </Col>

              {/* <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "brown" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("brown");
                }}
              >
                <div className="brown">
                  <span>Advance</span>
                  <h4>{statusCount.brown}</h4>
                </div>
              </Col> */}

              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "purple" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("purple");
                }}
              >
                <div className="purple">
                  <span>Created</span>
                  <h4>{statusCount.purple}</h4>
                </div>
              </Col>

              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "amber" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("amber");
                }}
              >
                <div className="amber">
                  <span>Assigned</span>
                  <h4>{statusCount.amber}</h4>
                </div>
              </Col>

              {/* <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "aqua" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("aqua");
                }}
              >
                <div className="aqua">
                  <span>Arrived</span>
                  <h4>{statusCount.aqua}</h4>
                </div>
              </Col> */}

              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "blue" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("blue");
                }}
              >
                <div className="blue">
                  <span>Started</span>
                  <h4>{statusCount.blue}</h4>
                </div>
              </Col>

              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "red" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("red");
                }}
              >
                <div className="red">
                  <span>Escalated</span>
                  <h4>{statusCount.red}</h4>
                </div>
              </Col>

              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "green" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("green");
                }}
              >
                <div className="green">
                  <span>Completed</span>
                  <h4>{statusCount.green}</h4>
                </div>
              </Col>

              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "grey" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("grey");
                }}
              >
                <div className="grey">
                  <span>Processed</span>
                  <h4>{statusCount.grey}</h4>
                </div>
              </Col>

              <Col
                span={2}
                className={`border rounded text-center p-2 mx-2 cursor-pointer ${
                  statusSelected === "black" ? "status-selected" : ""
                }`}
                onClick={() => {
                  setStatusPagePaths("black");
                }}
              >
                <div className="black">
                  <span>Cancelled</span>
                  <h4>{statusCount.black}</h4>
                </div>
              </Col>

              <Button
                className="beam-btn icon-btn"
                style={{
                  float: "right",
                  margin: "10px 0 10px 0",
                }}
                onClick={() => {
                  setShowTableFilterModal(!showTableFilterModal);
                }}
              >
                <FilterOutlined />
              </Button>

              <Button
                className="beam-btn icon-btn"
                style={{
                  float: "right",
                  margin: "10px 10px 10px 0",
                }}
                loading={projectFilterLoading || statusFilterLoading}
                onClick={() => {
                  setProjectFilterLoading(true);
                  searchPage();
                }}
              >
                {!projectFilterLoading && !statusFilterLoading && (
                  <RedoOutlined />
                )}
              </Button>
            </Row>
          )}

          <Table
            dataSource={tableDataSource}
            className={tableDataSource?.length > 0 ? "" : "table-empty"}
            onRow={(record) => {
              return {
                onDoubleClick: () => {
                  console.log("on double click row", record);
                },
                onClick: () => {
                  dispatch({
                    type: "SET_SELECTED_PAGE",
                    payload: { page: record?.action?.page },
                  });
                },
              };
            }}
            columns={tableColumns}
            scroll={{ x: true }}
            onChange={(e) => {
              console.log("event here", e);
            }}
            rowClassName={(record, index) => {
              if (record?.key === selectedPage?.page?.pageId)
                return "row-selected";
              return "";
            }}
          />
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default DashboardModal;
